/*
 * Ops statement + sheet compare: every rule lives here, pure (no DOM, no network), so the page is a
 * thin renderer. The uploaded sheet is parsed in the browser only; nothing is sent or saved.
 */
import { EM_DASH, MINUS, fmt_count, fmt_money, fmt_money_signed } from '../format';
import { cell_text, to_csv_text } from './csv';
import { session_href, session_who } from '../shared/session_link';

export const MAX_SHEET_BYTES = 2 * 1024 * 1024;
export const TOLERANCE = 1; // rupees: differences up to ₹1 are rounding, not a mismatch
export const NUMBER_TOLERANCE = 0.01; // day counts are whole: any real difference is a mismatch
const EPS = 1e-6;

const ID_KEY = 'teacherid';
// Our download appends it after the sheet template's columns; the compare skips it either way.
export const UUID_HEADER = 'Teacher UUID';
const UUID_KEY = 'teacheruuid';
// Month-labelled headers ("Grand Total\nSep-26", "Last month Payment ( Aug 26 )") match by prefix.
const PREFIX_KEYS = ['grandtotal', 'lastmonthpayment'];
const NAME_KEYS = ['teacherfullname', 'teachername'];
// Older sheets carried extra participants inside 1on1: with no Extra Participant column, compare 1on1 with it added.
const FOLDS_INTO = { extra_participant: 'one_on_one' };
// A drill-down line's Free Pass cover for a penalty column rides in stmt_parts as "<column>_pass".
const PASS_SUFFIX = '_pass';

// Case, space and punctuation insensitive ("&" reads as "and"): "Late Audio/Video Turn On" -> "lateaudiovideoturnon".
export const normalize_header = (h) =>
  String(h === null || h === undefined ? '' : h)
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '');

export const header_match_key = (h) => {
  const n = normalize_header(h);
  return PREFIX_KEYS.find((p) => n.startsWith(p)) || n;
};

const round2 = (n) => Math.round(n * 100) / 100;

/*
 * Sheet cell -> number. Tolerates ₹/Rs/INR, commas, spaces, U+2212, a trailing or leading sign and
 * accounting negatives "(1,200)". Blank (or a lone dash) is 0 for comparison. NaN when unreadable.
 */
export const parse_sheet_number = (raw) => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : NaN;
  let s = String(raw === null || raw === undefined ? '' : raw)
    .replace(/[\u00A0\s]/g, '')
    .replace(/\u2212/g, '-')
    .replace(/₹|rs\.?|inr/gi, '')
    .replace(/,/g, '');
  if (s === '' || s === '-') return 0;
  let neg = false;
  const paren = /^\((.*)\)$/.exec(s);
  if (paren) {
    neg = true;
    s = paren[1];
  }
  if (/^-?\d*\.?\d+-$/.test(s)) s = `-${s.slice(0, -1)}`; // "120-" (some exports)
  if (!/^[-+]?(\d+\.?\d*|\.\d+)$/.test(s)) return NaN;
  const n = Number(s);
  return neg ? -Math.abs(n) : n;
};

// Our wire value -> number; null/blank is 0 like a blank sheet cell.
const ours_number = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// "360", "360.0", " 360 " -> "360"; blank -> ''.
export const normalize_teacher_id = (v) => {
  const s = String(v === null || v === undefined ? '' : v).trim();
  return /^\d+\.0+$/.test(s) ? s.replace(/\.0+$/, '') : s;
};

/* RFC 4180 parser: quoted fields with commas, quotes ("") and newlines; CRLF or LF; BOM stripped. */
export const parse_csv = (text) => {
  const src = String(text || '').replace(/^\uFEFF/, '');
  const rows = [];
  let row = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 1;
        } else quoted = false;
      } else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += ch;
  }
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  // Spreadsheet exports pad with empty lines; they carry no data.
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
};

// File picked -> error text, or '' when it may be read.
export const check_sheet_file = (file) => {
  if (!file) return 'Choose a CSV file.';
  if (!/\.csv$/i.test(file.name || '')) return 'Upload a .csv file (File > Download > CSV).';
  if (file.size > MAX_SHEET_BYTES) return 'The file is over 2 MB. Upload only the statement sheet.';
  if (!file.size) return 'The file is empty.';
  return '';
};

/* ---------- statement (API) ---------- */

// Number columns carry no unit field; the contract's source text starts "days:" for the Free Pass ones.
export const number_unit = (col) => {
  if (!col || col.kind !== 'number') return '';
  return /^\s*days?\b/i.test(col.source || '') || /\bdays?\b/i.test(col.header || '') ? 'days' : '';
};

const unit_text = (n, unit) => {
  if (!unit) return '';
  return Math.abs(n) === 1 ? ` ${unit.replace(/s$/, '')}` : ` ${unit}`;
};

// A compared value in its column's unit: ₹ for money, "2 days" for a day count.
export const value_text = (n, col) => {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return EM_DASH;
  if (!col || col.kind === 'money') return fmt_money(n);
  const v = Number(n);
  const abs = Math.abs(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return `${v < 0 ? MINUS : ''}${abs}${unit_text(v, col.unit)}`;
};

// Signed like fmt_money_signed; zero carries no sign.
export const diff_text = (n, col) => {
  if (!col || col.kind === 'money') return fmt_money_signed(n);
  const v = Number(n);
  return v > 0 ? `+${value_text(v, col)}` : value_text(v, col);
};

const month_header = (statement, col) => {
  const key = header_match_key(col.header);
  if (key === 'grandtotal' && statement.grand_total_header) return statement.grand_total_header;
  if (key === 'lastmonthpayment' && statement.last_month_header) return statement.last_month_header;
  return col.header;
};

// A row's unreadable Free Pass days note; '' when none.
export const free_pass_unreadable_text = (n) =>
  n > 0 ? `${n} Free Pass ${n === 1 ? 'day' : 'days'} could not be read — tell engineering.` : '';

/*
 * Ops' sheet counts Free-Pass-covered penalties inside each penalty column and gives them back in Free Pass Day
 * Avail; the server sends charged only plus pass_cover (negative). Added here so the page, the download and the
 * compare all read like the sheet: the columns add up to Grand Total with Free Pass Day Avail added back.
 */
const with_pass_cover = (values, cover, keys) => {
  if (!cover || typeof cover !== 'object') return values;
  const out = { ...values };
  keys.forEach((k) => {
    const c = Number(cover[k]);
    if (c) out[k] = round2(ours_number(out[k]) + c);
  });
  return out;
};

// The statement as the page uses it: headers with the month labels, rows by teacher name.
export const statement_view = (data) => {
  const d = data || {};
  const columns = (Array.isArray(d.columns) ? d.columns : []).map((c) => {
    const col = {
      key: c.key,
      kind: c.kind === 'money' || c.kind === 'number' ? c.kind : 'text',
      source: c.source || '',
      header: month_header(d, c),
      // Added after the Sep-26 sheet template: an older upload may not have it (not a gap).
      optional: c.optional === true,
      // Other sheet headers for this column, and sheet columns whose values are added into it.
      aliases: Array.isArray(c.aliases) ? c.aliases.map(String) : [],
      parts: Array.isArray(c.parts) ? c.parts.map(String) : [],
      // true: the value is a sum of drill-down lines (stmt_parts); false: a derived total.
      from_lines: c.from_lines === true,
      // A penalty column (charged, negative): compared as a charge, Free Pass cover included (as Ops' sheet).
      deduction: c.deduction === true
    };
    return { ...col, unit: number_unit(col) };
  });
  const unmapped = Array.isArray(d.unmapped) ? d.unmapped : [];
  const deductions = columns.filter((c) => c.deduction).map((c) => c.key);
  const rows = (Array.isArray(d.rows) ? d.rows : []).map((r) => ({
    teacher_id: normalize_teacher_id(r.teacher_id),
    teacher_uuid: r.teacher_uuid || '',
    // false: the month's lines do not add up to its Grand Total (waiting for a recompute).
    ties_out: r.ties_out !== false,
    // >0: Free Pass days the server could not read (it then sends ties_out=false); a row field, never compared.
    free_pass_days_unreadable: Math.max(0, Math.trunc(Number(r.free_pass_days_unreadable)) || 0),
    values: with_pass_cover(r.values || {}, r.pass_cover, deductions)
  }));
  const name_cols = NAME_KEYS.map((k) =>
    columns.find((c) => header_match_key(c.header) === k)
  ).filter(Boolean);
  const name_of = (r) =>
    name_cols.map((c) => r.values[c.key]).find((v) => v !== null && v !== undefined && v !== '') ||
    '';
  const named = rows.map((r) => ({ ...r, teacher_name: String(name_of(r)) }));
  named.sort(
    (a, b) =>
      a.teacher_name.localeCompare(b.teacher_name, 'en', { sensitivity: 'base' }) ||
      a.teacher_id.localeCompare(b.teacher_id, 'en', { numeric: true })
  );
  return { month: d.month || '', columns, rows: named, unmapped };
};

// Download: exactly the sheet's headers and order, plain numbers, blank for null; Teacher UUID last.
export const statement_csv = (view, opts) =>
  to_csv_text(
    [...view.columns.map((c) => c.header), UUID_HEADER],
    view.rows.map((r) => [
      ...view.columns.map((c) => cell_text(r.values[c.key], c.kind)),
      r.teacher_uuid || ''
    ]),
    opts
  );

/* ---------- sheet ---------- */

/*
 * CSV text -> { error } or { headers, by_id, duplicate_ids, rows_without_id }. The first row is the
 * header row; teacher_id is required.
 */
export const read_sheet = (text) => {
  const rows = parse_csv(text);
  if (!rows.length) return { error: 'The sheet has no rows.' };
  const headers = rows[0];
  const id_index = headers.findIndex((h) => header_match_key(h) === ID_KEY);
  if (id_index === -1)
    return { error: 'The sheet has no teacher_id column, so rows cannot be matched.' };
  const by_id = new Map();
  const duplicate_ids = [];
  let rows_without_id = 0;
  rows.slice(1).forEach((cells) => {
    const id = normalize_teacher_id(cells[id_index]);
    if (!id) {
      rows_without_id += 1;
      return;
    }
    if (by_id.has(id)) {
      if (!duplicate_ids.includes(id)) duplicate_ids.push(id);
      return; // first row wins
    }
    by_id.set(id, cells);
  });
  if (!by_id.size) return { error: 'The sheet has a header row but no teacher rows.' };
  return { headers, id_index, by_id, duplicate_ids, rows_without_id };
};

/* ---------- compare ---------- */

// statement + sheet -> cells; diff = ours - sheet (0.01), differs past the column's tolerance or on a non-number.
export const compare_sheet = (view, sheet, tolerance = TOLERANCE) => {
  const unmapped_keys = new Set(view.unmapped.map((u) => u.key));
  // A sheet header matches our header first, else our key ("one_on_one", "One On One"), else an alias.
  const ours_by_match = new Map();
  const claim = (k, c) => {
    if (k && !ours_by_match.has(k)) ours_by_match.set(k, c);
  };
  view.columns.forEach((c) => claim(header_match_key(c.header), c));
  view.columns.forEach((c) => claim(normalize_header(c.key), c));
  view.columns.forEach((c) => (c.aliases || []).forEach((a) => claim(header_match_key(a), c)));
  const part_of = new Map();
  view.columns.forEach((c) =>
    (c.parts || []).forEach((p) => {
      const k = header_match_key(p);
      if (k && !ours_by_match.has(k) && !part_of.has(k)) part_of.set(k, c);
    })
  );
  const unmapped_by_match = new Map();
  view.unmapped.forEach((u) => {
    unmapped_by_match.set(header_match_key(u.header), u);
    unmapped_by_match.set(normalize_header(u.key), u);
  });

  // Sheet column index -> our column (compared ones only); the rest is sorted into the lists.
  const main_at = new Map(); // our key -> { index, header } of its sheet column
  const parts_at = new Map(); // our key -> [{ index, header }] of sheet columns added into it
  const unrecognised = [];
  // One per header (repeats folded): what the column-mapping controls offer.
  const unrecognised_columns = [];
  const ignored_keys = new Set(view.ignored || []);
  const ignored_in_sheet = [];
  const seen = new Set();
  const seen_parts = new Set();
  sheet.headers.forEach((h, i) => {
    if (i === sheet.id_index) return;
    const k = header_match_key(h);
    if (!k || k === UUID_KEY) return; // unnamed padding column / our trailing id column
    if (ignored_keys.has(k)) {
      ignored_in_sheet.push(String(h));
      return;
    }
    const col = ours_by_match.get(k);
    const part_col = col ? null : part_of.get(k);
    if (part_col) {
      if (seen_parts.has(k)) {
        unrecognised.push(`${h} (repeated column)`);
        return;
      }
      seen_parts.add(k);
      parts_at.set(part_col.key, [
        ...(parts_at.get(part_col.key) || []),
        { index: i, header: String(h) }
      ]);
      return;
    }
    if (!col) {
      if (unmapped_by_match.has(k)) return;
      unrecognised.push(String(h));
      if (!unrecognised_columns.some((u) => u.match_key === k))
        unrecognised_columns.push({ header: String(h), match_key: k });
      return;
    }
    if (seen.has(col.key)) {
      unrecognised.push(`${h} (repeated column)`);
      return;
    }
    seen.add(col.key);
    if (col.kind === 'text' || unmapped_keys.has(col.key) || k === ID_KEY) return;
    main_at.set(col.key, { index: i, header: String(h) });
  });
  const comparable = (c) =>
    c.kind !== 'text' && !unmapped_keys.has(c.key) && header_match_key(c.header) !== ID_KEY;
  // In sheet order; a column with only its parts in the sheet still compares (the main counts 0).
  const compared = view.columns
    .filter((c) => comparable(c) && (main_at.has(c.key) || parts_at.has(c.key)))
    .map((c) => ({
      index: main_at.has(c.key) ? main_at.get(c.key).index : null,
      parts: (parts_at.get(c.key) || []).map((p) => p.index),
      first: Math.min(
        ...[main_at.get(c.key), ...(parts_at.get(c.key) || [])].filter(Boolean).map((x) => x.index)
      ),
      col: c
    }))
    .sort((a, b) => a.first - b.first);
  const combined_in_sheet = compared
    .filter((c) => c.parts.length)
    .map(({ col }) => ({
      headers: [main_at.get(col.key), ...parts_at.get(col.key)]
        .filter(Boolean)
        .map((x) => x.header),
      into: col.header
    }));
  const absent = view.columns
    .filter(comparable)
    .filter((c) => !seen.has(c.key) && !parts_at.has(c.key));
  const missing_in_sheet = absent.filter((c) => !c.optional).map((c) => c.header);
  const folded = absent.filter((c) => FOLDS_INTO[c.key] && seen.has(FOLDS_INTO[c.key]));
  const ours_of = (r, col) =>
    folded
      .filter((f) => FOLDS_INTO[f.key] === col.key)
      .reduce((sum, f) => sum + ours_number(r.values[f.key]), ours_number(r.values[col.key]));
  // A folded column was compared inside its parent, so it is listed apart from the uncompared ones.
  const newer_not_in_sheet = absent
    .filter((c) => c.optional && !folded.includes(c))
    .map((c) => c.header);
  const folded_in_sheet = folded.map((c) => ({
    header: c.header,
    into: (view.columns.find((x) => x.key === FOLDS_INTO[c.key]) || {}).header || FOLDS_INTO[c.key]
  }));

  // Full name first, then the short name: the first one filled in.
  const name_indexes = NAME_KEYS.map((k) =>
    sheet.headers.findIndex((h) => header_match_key(h) === k)
  ).filter((i) => i !== -1);
  const sheet_name = (cells) =>
    name_indexes.map((i) => String(cells[i] || '').trim()).find(Boolean) || '';

  const uuid_index = sheet.headers.findIndex((h) => header_match_key(h) === UUID_KEY);
  const ours_ids = new Set(view.rows.map((r) => r.teacher_id));
  const cells = [];
  view.rows.forEach((r) => {
    const s = sheet.by_id.get(r.teacher_id);
    if (!s) return;
    compared.forEach(({ index, parts, col }) => {
      const text_at = (i) => (i === null || s[i] === undefined ? '' : String(s[i]));
      // Parts add into the main cell; a blank part (or a missing main) counts 0.
      const texts = [index, ...parts].filter((i) => i !== null).map(text_at);
      const sheet_text = parts.length ? texts.filter((t) => t.trim() !== '').join(' + ') : texts[0];
      const sheet_n = texts.reduce((sum, t) => sum + parse_sheet_number(t), 0);
      const invalid = Number.isNaN(sheet_n);
      // A penalty column compares the charge as a positive amount (Free Pass cover already in it, as the sheet).
      const charge = col.deduction === true;
      const sign = charge ? -1 : 1;
      const ours = round2(sign * ours_of(r, col));
      const sheet_v = sign * sheet_n;
      const diff = invalid ? null : round2(ours - sheet_v);
      const allowed = col.kind === 'money' ? tolerance : NUMBER_TOLERANCE;
      cells.push({
        teacher_id: r.teacher_id,
        teacher_name: r.teacher_name,
        teacher_uuid: r.teacher_uuid,
        ties_out: r.ties_out,
        free_pass_days_unreadable: r.free_pass_days_unreadable,
        key: col.key,
        header: col.header,
        kind: col.kind,
        unit: col.unit,
        from_lines: col.from_lines === true,
        // Keys whose stmt_parts make up `ours` (a folded Extra Participant rides inside 1on1).
        fold_keys: [
          col.key,
          ...(charge ? [`${col.key}${PASS_SUFFIX}`] : []),
          ...folded.filter((f) => FOLDS_INTO[f.key] === col.key).map((f) => f.key)
        ],
        // Charge columns: positive = charged; line amounts flip to match, and the money-direction diff is -diff.
        charge,
        line_sign: sign,
        pay_diff: invalid ? null : round2(sign * (ours - sheet_v)),
        folded_headers: folded.filter((f) => FOLDS_INTO[f.key] === col.key).map((f) => f.header),
        sheet: invalid ? null : round2(sheet_v),
        sheet_text,
        ours,
        diff,
        invalid,
        differs: invalid || Math.abs(diff) > allowed + EPS
      });
    });
  });

  const only_in_sheet = [...sheet.by_id.entries()]
    .filter(([id]) => !ours_ids.has(id))
    .map(([id, c]) => ({
      teacher_id: id,
      teacher_name: sheet_name(c),
      teacher_uuid: uuid_index === -1 ? '' : String(c[uuid_index] || '').trim()
    }));
  const only_in_ours = view.rows
    .filter((r) => !sheet.by_id.has(r.teacher_id))
    .map((r) => ({
      teacher_id: r.teacher_id,
      teacher_name: r.teacher_name,
      teacher_uuid: r.teacher_uuid,
      ties_out: r.ties_out,
      free_pass_days_unreadable: r.free_pass_days_unreadable
    }));
  // ties_out=false: a warning per teacher (lines do not add up to Grand Total yet), never a blocker.
  const not_tying_out = view.rows
    .filter((r) => !r.ties_out)
    .map((r) => ({
      teacher_id: r.teacher_id,
      teacher_name: r.teacher_name,
      free_pass_days_unreadable: r.free_pass_days_unreadable
    }));
  const not_compared = view.unmapped.map((u) => ({ header: u.header, reason: u.reason || '' }));

  return {
    cells,
    summary: summarize(
      cells,
      compared.map((c) => c.col)
    ),
    only_in_sheet,
    only_in_ours,
    not_tying_out,
    unrecognised,
    unrecognised_columns,
    ignored_in_sheet,
    // our key -> the sheet header compared as that column (not its parts)
    matched: new Map([...main_at.entries()].map(([key, m]) => [key, m.header])),
    not_compared,
    missing_in_sheet,
    newer_not_in_sheet,
    folded_in_sheet,
    combined_in_sheet,
    duplicate_ids: sheet.duplicate_ids,
    rows_without_id: sheet.rows_without_id
  };
};

const is_money = (c) => !c.kind || c.kind === 'money';

export const NET_PAYABLE_KEY = 'net_payable';
export const GRAND_TOTAL_KEY = 'grand_total';
export const NET_BASIS_LABEL = {
  net_payable: 'Net Payable',
  grand_total: 'Grand Total',
  lines: 'Line columns'
};
export const NET_BASIS_TEXT = {
  net_payable: 'Net Payable difference',
  grand_total: 'Grand Total difference (Net Payable not compared or unreadable)',
  lines: 'Sum of line columns (no Net Payable or Grand Total on the sheet)'
};

/*
 * A teacher's bottom-line gap from all their compared cells: Net Payable's diff, else Grand Total's,
 * else the sum of differing line (from_lines) money cells. Never a sum across totals: they double count.
 */
export const teacher_net = (cells) => {
  const readable = (key) => cells.find((c) => c.key === key && !c.invalid);
  const total = readable(NET_PAYABLE_KEY) || readable(GRAND_TOTAL_KEY);
  if (total) return { net: total.differs ? round2(total.diff) : 0, net_basis: total.key };
  const lines = cells.filter((c) => c.differs && !c.invalid && c.from_lines && is_money(c));
  // pay_diff: a charge column's diff in money terms (charging more lowers pay).
  const pay = (c) => (typeof c.pay_diff === 'number' ? c.pay_diff : c.diff);
  return { net: round2(lines.reduce((s, c) => s + pay(c), 0)), net_basis: 'lines' };
};

// Sum of each teacher's teacher_net (same basis rule per teacher).
const nets_by_teacher = (cells) => {
  const by = new Map();
  cells.forEach((c) => by.set(c.teacher_id, [...(by.get(c.teacher_id) || []), c]));
  return [...by.values()].map(teacher_net);
};
const category = (header, kind = 'money', unit = '') => ({
  header,
  kind,
  unit,
  teachers: 0,
  positive: 0,
  negative: 0,
  net: 0,
  invalid: 0
});

// Per-category totals of differing cells in the category's unit; net is money only (days never add to ₹).
export const summarize = (cells, columns = []) => {
  const teachers = new Set(cells.map((c) => c.teacher_id));
  const diff_teachers = new Set(cells.filter((c) => c.differs).map((c) => c.teacher_id));
  const by = new Map(
    columns.map((c) =>
      typeof c === 'string' ? [c, category(c)] : [c.header, category(c.header, c.kind, c.unit)]
    )
  );
  cells
    .filter((c) => c.differs)
    .forEach((c) => {
      const t = by.get(c.header) || category(c.header, c.kind || 'money', c.unit || '');
      t.teachers += 1;
      if (c.invalid) t.invalid += 1;
      else if (c.diff > 0) t.positive = round2(t.positive + c.diff);
      else t.negative = round2(t.negative + c.diff);
      t.net = round2(t.positive + t.negative);
      by.set(c.header, t);
    });
  return {
    teachers_compared: teachers.size,
    teachers_with_differences: diff_teachers.size,
    // A cell we could not read is counted apart, not as a difference.
    differences: cells.filter((c) => c.differs && !c.invalid).length,
    // Day-count differences: counted above, kept out of the money net.
    number_differences: cells.filter((c) => c.differs && !c.invalid && !is_money(c)).length,
    unreadable: cells.filter((c) => c.invalid).length,
    // Only categories that differ, in sheet order.
    categories: [...by.values()].filter((t) => t.teachers > 0),
    // Per-teacher bottom lines added up; the per-category nets above stay per column.
    net: round2(nets_by_teacher(cells).reduce((s, t) => s + t.net, 0))
  };
};

/* ---------- results view ---------- */

export const DEFAULT_SORT = { key: 'abs_diff', dir: 'desc' };

const matches_teacher = (c, q) => {
  const t = String(q || '')
    .trim()
    .toLowerCase();
  return !t || c.teacher_id === t || c.teacher_name.toLowerCase().includes(t);
};

// Header click: the same column flips direction; a new one starts at its natural order.
export const next_sort = (sort, key) => {
  if (sort && sort.key === key) return { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' };
  return key === 'teacher' ? { key, dir: 'asc' } : DEFAULT_SORT;
};

// filters: { category: header|'', teacher: text, only_differences: bool }
export const filter_cells = (
  cells,
  { category = '', teacher = '', only_differences = true } = {}
) =>
  cells.filter(
    (c) =>
      (!only_differences || c.differs) &&
      (!category || c.header === category) &&
      matches_teacher(c, teacher)
  );

// abs_diff (an unreadable sheet cell sorts as the largest) or teacher (name, then category order).
export const sort_cells = (cells, sort = DEFAULT_SORT) => {
  const dir = sort.dir === 'asc' ? 1 : -1;
  const abs = (c) => (c.invalid ? Infinity : Math.abs(c.diff));
  return cells
    .map((c, i) => ({ c, i }))
    .sort((a, b) => {
      if (sort.key === 'teacher') {
        const by =
          a.c.teacher_name.localeCompare(b.c.teacher_name, 'en', { sensitivity: 'base' }) ||
          a.c.teacher_id.localeCompare(b.c.teacher_id, 'en', { numeric: true });
        return by * dir || a.i - b.i;
      }
      const d = abs(a.c) === abs(b.c) ? 0 : abs(a.c) > abs(b.c) ? 1 : -1;
      return d * dir || a.i - b.i;
    })
    .map((x) => x.c);
};

export const DIFF_HEADERS = [
  'Teacher ID',
  'Teacher',
  'Category',
  'Your sheet',
  'Our calculation',
  'Difference (ours - sheet)',
  UUID_HEADER
];

// Money to the paisa; a day count as a plain number ("-2", not "-2.00").
const csv_kind = (c) => (c.kind === 'number' ? 'number' : 'money');

export const differences_csv = (cells, opts) =>
  to_csv_text(
    DIFF_HEADERS,
    cells.map((c) => [
      c.teacher_id,
      c.teacher_name,
      c.header,
      c.invalid ? c.sheet_text : cell_text(c.sheet, csv_kind(c)),
      cell_text(c.ours, csv_kind(c)),
      c.invalid ? '' : cell_text(c.diff, csv_kind(c)),
      c.teacher_uuid || ''
    ]),
    opts
  );

const pad = (n) => String(n).padStart(2, '0');
// "teacher-payments-statement-2026-09-20261101-1530.csv" (IST stamp).
export const file_name = (kind, month, now = new Date()) => {
  const d = new Date(now.getTime() + 330 * 60000);
  const stamp = `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}-${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}`;
  return `teacher-payments-${kind}-${month || 'month'}-${stamp}.csv`;
};

/* ---------- column mapping (shared by Ops, saved in goserver) ---------- */

export const MAP_ACTIONS = ['ignore', 'same', 'add'];
const TO_API = { ignore: 'IGNORE', same: 'SAME_AS', add: 'ADD_INTO' };
const FROM_API = { IGNORE: 'ignore', SAME_AS: 'same', ADD_INTO: 'add' };

// Our columns a sheet column may stand for or add into: the compared money and day columns.
export const mapping_targets = (view) => {
  const unmapped_keys = new Set(view.unmapped.map((u) => u.key));
  return view.columns
    .filter(
      (c) => c.kind !== 'text' && !unmapped_keys.has(c.key) && header_match_key(c.header) !== ID_KEY
    )
    .map((c) => ({ key: c.key, header: c.header, kind: c.kind }));
};

// <select> value <-> choice: '' (not mapped), 'ignore', 'same:<key>', 'add:<key>'.
export const choice_value = (choice) => {
  if (!choice || !MAP_ACTIONS.includes(choice.action)) return '';
  return choice.action === 'ignore' ? 'ignore' : `${choice.action}:${choice.into}`;
};
export const parse_choice = (value) => {
  const v = String(value || '');
  if (v === 'ignore') return { action: 'ignore' };
  const m = /^(same|add):(.+)$/.exec(v);
  return m ? { action: m[1], into: m[2] } : null;
};

const valid_choice = (c) =>
  !!c &&
  typeof c === 'object' &&
  (c.action === 'ignore' ||
    ((c.action === 'same' || c.action === 'add') && typeof c.into === 'string' && !!c.into));

// mapping: { [header_match_key]: { action, into?, by_name?, at_ist? } }; a null choice removes it.
export const set_mapping_choice = (mapping, match_key, choice) => {
  const next = { ...(mapping || {}) };
  if (valid_choice(choice))
    next[match_key] = {
      action: choice.action,
      ...(choice.action === 'ignore' ? {} : { into: choice.into }),
      ...(choice.by_name ? { by_name: choice.by_name } : {}),
      ...(choice.at_ist ? { at_ist: choice.at_ist } : {})
    };
  else delete next[match_key];
  return next;
};

// One API mapping -> [match key, choice]; keyed by our own header rule so matching stays one rule.
export const mapping_from_api = (m) => {
  const choice = {
    action: FROM_API[m && m.action],
    into: (m && m.column_key) || undefined,
    by_name: (m && m.by_name) || '',
    at_ist: (m && m.at_ist) || ''
  };
  const key = header_match_key(m && m.sheet_header);
  return key && valid_choice(choice) ? [key, choice] : null;
};

// GET /statement/mappings data -> { mapping, max_mappings }; unknown actions are dropped.
export const mappings_view = (data) => {
  const list = Array.isArray(data && data.mappings) ? data.mappings : [];
  const mapping = list
    .map(mapping_from_api)
    .filter(Boolean)
    .reduce((acc, [k, c]) => set_mapping_choice(acc, k, c), {});
  const limits = (data && data.limits) || {};
  return { mapping, max_mappings: Number(limits.max_mappings) || 0 };
};

// POST /statement/mappings/set body; IGNORE carries no column_key.
export const mapping_set_body = (sheet_header, choice) => ({
  sheet_header: String(sheet_header).trim(),
  action: TO_API[choice.action],
  ...(choice.action === 'ignore' ? {} : { column_key: choice.into })
});

// 400 INVALID_REQUEST carries fields: [{ field, code, message }]; 409/500 the same envelope without it.
const field_errors = (body) =>
  (Array.isArray(body && body.fields) ? body.fields : [])
    .map((x) => (x && x.message) || '')
    .filter(Boolean);

// A failed set/remove -> the sentence shown under the control.
export const mapping_error_text = (e) => {
  const body = (e && e.response && e.response.data) || {};
  const code = body.error_code || '';
  if (code === 'MAPPING_BUILT_IN')
    return 'This header already matches one of our columns, so it cannot be mapped.';
  if (code === 'MAPPING_LIMIT')
    return 'The limit of saved mappings is reached. Remove one you no longer need first.';
  const fields = field_errors(body);
  if (fields.length) return fields.join(' ');
  if (code === 'LOOKUP_FAILED') return 'Could not save the mapping. Try again.';
  return body.message || 'Could not save the mapping. Try again.';
};

/*
 * The saved mapping applied to the columns this sheet left unrecognised (base = compare_sheet on
 * the plain view). Same as -> an alias, Add into -> a part, Ignore -> skipped. A Same as onto a
 * column the sheet already fills (or another Same as took) is a conflict and is not applied.
 * Returns { view, mapped: [{ header, action, into, into_header }], conflicts: [{ header, text }] }.
 */
const who_when = (c) => (c.by_name ? { by_name: c.by_name, at_ist: c.at_ist || '' } : {});
export const apply_mapping = (view, base, mapping) => {
  const m = mapping || {};
  const targets = new Map(mapping_targets(view).map((t) => [t.key, t]));
  const aliases = new Map();
  const parts = new Map();
  const ignored = [];
  const mapped = [];
  const conflicts = [];
  const taken = new Map(base.matched || []);
  (base.unrecognised_columns || []).forEach(({ header, match_key }) => {
    const choice = m[match_key];
    if (!valid_choice(choice)) return;
    if (choice.action === 'ignore') {
      ignored.push(match_key);
      mapped.push({ header, action: 'ignore', into: '', into_header: '', ...who_when(choice) });
      return;
    }
    const target = targets.get(choice.into);
    if (!target) {
      conflicts.push({
        header,
        text: `${header}: the column it was mapped to is not compared any more`
      });
      return;
    }
    if (choice.action === 'same') {
      if (taken.has(target.key)) {
        conflicts.push({
          header,
          text: `${header}: ${target.header} already matches your column ${taken.get(target.key)}; choose Add into instead`
        });
        return;
      }
      taken.set(target.key, header);
      aliases.set(target.key, [...(aliases.get(target.key) || []), header]);
    } else parts.set(target.key, [...(parts.get(target.key) || []), header]);
    mapped.push({
      header,
      action: choice.action,
      into: target.key,
      into_header: target.header,
      ...who_when(choice)
    });
  });
  return {
    view: {
      ...view,
      ignored,
      columns: view.columns.map((c) =>
        aliases.has(c.key) || parts.has(c.key)
          ? {
              ...c,
              aliases: [...(c.aliases || []), ...(aliases.get(c.key) || [])],
              parts: [...(c.parts || []), ...(parts.get(c.key) || [])]
            }
          : c
      )
    },
    mapped,
    conflicts
  };
};

// "Invoice correction → ignored (Ops One, 2026-11-02 23:40)", "Bonus X → added into Additional Payments".
export const mapped_text = (m) => {
  const into = String(m.into_header || '').replace(/\s+/g, ' ');
  const what =
    m.action === 'ignore'
      ? `${m.header} → ignored`
      : m.action === 'same'
        ? `${m.header} → same as ${into}`
        : `${m.header} → added into ${into}`;
  const by = [m.by_name, String(m.at_ist || '').replace(/:\d\d$/, '')].filter(Boolean).join(', ');
  return by ? `${what} (${by})` : what;
};

/* ---------- by teacher ---------- */

export const GROUP_COMPARED = 'compared';
export const GROUP_ONLY_SHEET = 'only_in_sheet';
export const GROUP_ONLY_OURS = 'only_in_ours';
export const GROUP_LABELS = {
  [GROUP_COMPARED]: 'Compared',
  [GROUP_ONLY_SHEET]: 'Only in your sheet',
  [GROUP_ONLY_OURS]: 'Only in ours'
};
const WORST_SHOWN = 3;
const cell_size = (c) => (c.invalid ? Infinity : Math.abs(c.diff));
const by_size = (a, b) =>
  cell_size(a) === cell_size(b) ? 0 : cell_size(a) > cell_size(b) ? -1 : 1;

/*
 * One row per teacher: differing = cells past tolerance (unreadable included), net = teacher_net
 * (bottom line + its basis), worst = the largest differing cells. Cells: differing first, largest first.
 */
export const by_teacher = (result) => {
  const rows = new Map();
  result.cells.forEach((c, i) => {
    if (!rows.has(c.teacher_id))
      rows.set(c.teacher_id, {
        group: GROUP_COMPARED,
        teacher_id: c.teacher_id,
        teacher_name: c.teacher_name,
        teacher_uuid: c.teacher_uuid || '',
        ties_out: c.ties_out,
        free_pass_days_unreadable: c.free_pass_days_unreadable,
        cells: []
      });
    rows.get(c.teacher_id).cells.push({ ...c, order: i });
  });
  const compared = [...rows.values()].map((t) => {
    const diffs = t.cells.filter((c) => c.differs);
    const cells = [...t.cells]
      .sort((a, b) => Number(b.differs) - Number(a.differs) || by_size(a, b) || a.order - b.order)
      .map(({ order, ...c }) => c);
    return {
      ...t,
      cells,
      differing: diffs.length,
      unreadable: diffs.filter((c) => c.invalid).length,
      day_differences: diffs.filter((c) => !c.invalid && !is_money(c)).length,
      ...teacher_net(t.cells),
      worst: cells.filter((c) => c.differs).slice(0, WORST_SHOWN)
    };
  });
  const only = (group, list) =>
    list.map((t) => ({
      group,
      teacher_id: t.teacher_id,
      teacher_name: t.teacher_name || '',
      teacher_uuid: t.teacher_uuid || '',
      ties_out: t.ties_out !== false,
      free_pass_days_unreadable: t.free_pass_days_unreadable || 0,
      cells: [],
      differing: 0,
      unreadable: 0,
      day_differences: 0,
      net: 0,
      net_basis: '',
      worst: []
    }));
  return {
    compared,
    only_in_sheet: only(GROUP_ONLY_SHEET, result.only_in_sheet),
    only_in_ours: only(GROUP_ONLY_OURS, result.only_in_ours)
  };
};

export const DEFAULT_TEACHER_SORT = { key: 'differing', dir: 'desc' };

// Header click: the same column flips; a new one starts at its natural order (name A-Z, else largest).
export const next_teacher_sort = (sort, key) => {
  if (sort && sort.key === key) return { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: key === 'teacher' ? 'asc' : 'desc' };
};

const by_name = (a, b) =>
  a.teacher_name.localeCompare(b.teacher_name, 'en', { sensitivity: 'base' }) ||
  a.teacher_id.localeCompare(b.teacher_id, 'en', { numeric: true });

// differing (then |net|), net (|net|, then differing) or teacher; ties fall back to the name.
export const sort_teachers = (rows, sort = DEFAULT_TEACHER_SORT) => {
  const dir = sort.dir === 'asc' ? 1 : -1;
  const cmp = (x, y) => (x === y ? 0 : x > y ? 1 : -1);
  return [...rows].sort((a, b) => {
    if (sort.key === 'teacher') return by_name(a, b) * dir;
    const first =
      sort.key === 'net'
        ? cmp(Math.abs(a.net), Math.abs(b.net)) || cmp(a.differing, b.differing)
        : cmp(a.differing, b.differing) || cmp(Math.abs(a.net), Math.abs(b.net));
    return first * dir || by_name(a, b);
  });
};

export const filter_teachers = (rows, { teacher = '', only_differences = false } = {}) =>
  rows.filter((t) => (!only_differences || t.differing > 0) && matches_teacher(t, teacher));

// "Free Pass Days Used −2 days", "1on1 +₹1,280.00", "GST not a number".
export const worst_text = (c) =>
  `${String(c.header).replace(/\s+/g, ' ')} ${c.invalid ? 'not a number' : diff_text(c.diff, c)}`;

export const TEACHER_HEADERS = [
  'Group',
  'Teacher ID',
  'Teacher',
  'Columns differing',
  'Cells not a number',
  'Net difference (ours - sheet, money)',
  'Net difference basis',
  'Worst columns',
  UUID_HEADER
];

export const by_teacher_csv = (rows, opts) =>
  to_csv_text(
    TEACHER_HEADERS,
    rows.map((t) => [
      GROUP_LABELS[t.group] || t.group,
      t.teacher_id,
      t.teacher_name,
      t.group === GROUP_COMPARED ? String(t.differing) : '',
      t.group === GROUP_COMPARED ? String(t.unreadable) : '',
      t.group === GROUP_COMPARED ? cell_text(t.net, 'money') : '',
      t.group === GROUP_COMPARED ? NET_BASIS_TEXT[t.net_basis] || '' : '',
      t.worst
        .map((c) =>
          c.invalid ? `${c.header}: not a number` : `${c.header}: ${cell_text(c.diff, csv_kind(c))}`
        )
        .join('; '),
      t.teacher_uuid || ''
    ]),
    opts
  );

// "4 teachers compared · 1 with any difference · 3 exact".
export const summary_line = (summary) => {
  const n = summary.teachers_compared;
  const m = summary.teachers_with_differences;
  return `${fmt_count(n)} ${n === 1 ? 'teacher' : 'teachers'} compared · ${fmt_count(m)} with any difference · ${fmt_count(n - m)} exact`;
};

/* ---------- the classes behind a difference ---------- */

// The drill-down needs a category; these together cover every line of a teacher's month (overview_enums).
export const LINE_CATEGORIES = [
  'ONE_ON_ONE_MEMBER',
  'TRIAL',
  'GROUP_CLASS',
  'UNATTRIBUTED',
  'ADDITIONAL',
  'EXTRA_PARTICIPANT'
];

const key_list = (keys) => (Array.isArray(keys) ? keys : [keys]);
const part_amount = (r, keys) =>
  (Array.isArray(r.stmt_parts) ? r.stmt_parts : [])
    .filter((p) => p && key_list(keys).includes(p.column))
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
const has_part = (r, keys) =>
  Array.isArray(r.stmt_parts) && r.stmt_parts.some((p) => p && key_list(keys).includes(p.column));

const line_label = (r) => {
  if (r.kind === 'additional') {
    const what = r.category_label || r.category || 'Additional payment';
    return r.session_label ? `${what} · ${r.session_label}` : what;
  }
  const who = session_who(r.session_kind, r.session_label, r.student_name);
  return who.name ? `${who.noun}: ${who.name}` : who.noun;
};

/*
 * Drill-down responses (one per category) -> the lines feeding one statement column, oldest first.
 * An additional payment opens its related session when it has one.
 */
// `key` is one column key or the cell's fold_keys.
// `sign` -1 shows a charge column's lines as positive charges (the cell's line_sign).
export const column_lines = (responses, key, sign = 1) => {
  const lines = [];
  (Array.isArray(responses) ? responses : []).forEach((d) => {
    ((d && d.days) || []).forEach((day) => {
      ((day && day.rows) || []).forEach((r) => {
        if (!r || !has_part(r, key)) return;
        const is_add = r.kind === 'additional';
        const uuid = (is_add ? r.related_session_uuid : r.session_uuid) || '';
        lines.push({
          key: `${r.kind}-${r.penalty_uuid || r.uuid || ''}-${r.session_uuid || ''}-${r.item_type || r.penalty_type || ''}-${lines.length}`,
          time_ist: (is_add ? r.entry_at_ist : r.start_time_ist) || '',
          label: line_label(r),
          status: (is_add ? r.status : r.session_status) || '',
          amount: round2(sign * part_amount(r, key)),
          // Charged or not, a Free-Pass-covered penalty still counts in Ops' penalty column.
          covered: key_list(key).some((k) => k.endsWith(PASS_SUFFIX) && has_part(r, k)),
          rule_name: r.rule_name || r.penalty_type || r.category_label || '',
          explain: (r.explain && r.explain.formula) || '',
          session_uuid: uuid,
          session_kind: r.session_kind || '',
          href: session_href(uuid, r.session_kind)
        });
      });
    });
  });
  return lines.sort((a, b) => (a.time_ist === b.time_ist ? 0 : a.time_ist < b.time_ist ? -1 : 1));
};

export const lines_total = (lines) => round2(lines.reduce((s, l) => s + l.amount, 0));

// The footer: the lines against ours and the sheet; adds_up=false when the lines miss our value.
export const tie_out = (lines, cell) => {
  const n = lines.length;
  const total = lines_total(lines);
  const what = n === 1 ? 'This 1 line adds' : `These ${fmt_count(n)} lines add`;
  const sheet = cell.invalid ? `“${cell.sheet_text}” (not a number)` : value_text(cell.sheet, cell);
  const diff = cell.invalid ? EM_DASH : diff_text(cell.diff, cell);
  const adds_up = Math.abs(total - Number(cell.ours || 0)) <= NUMBER_TOLERANCE;
  const folded = cell.folded_headers || [];
  const includes = folded.length ? ` (includes ${folded.join(', ')})` : '';
  return {
    adds_up,
    text: `${what} up to ${value_text(total, cell)}${includes}; your sheet says ${sheet}; difference ${diff}`,
    mismatch: adds_up
      ? ''
      : `These lines do not add up to our ${value_text(cell.ours, cell)} (they are ${diff_text(round2(total - Number(cell.ours || 0)), cell)} off) — tell engineering.`
  };
};

export const LINE_HEADERS = [
  'Teacher ID',
  'Column',
  'Date/time (IST)',
  'Class / student',
  'Status',
  'Amount',
  'Rule',
  'Explain',
  'Session UUID',
  UUID_HEADER
];

export const lines_csv = (lines, t, cell, opts) =>
  to_csv_text(
    LINE_HEADERS,
    lines.map((l) => [
      t.teacher_id,
      String(cell.header).replace(/\s+/g, ' '),
      l.time_ist,
      l.label,
      l.status,
      cell_text(l.amount, cell.kind === 'number' ? 'number' : 'money'),
      l.rule_name,
      l.explain,
      l.session_uuid,
      t.teacher_uuid || ''
    ]),
    opts
  );
